
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { useTranslatedText } from "@/hooks/useTranslate";
import { supabase } from "@/integrations/supabase/client";

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    direccion: "",
    telefono: "",
    servicios: [],
    mensaje: ""
  });

  // Traducciones para el formulario
  const emailTitleText = useTranslatedText("Email");
  const nombreText = useTranslatedText("Nombre");
  const emailText = useTranslatedText("E-mail");
  const direccionText = useTranslatedText("Dirección");
  const telefonoText = useTranslatedText("Teléfono");
  const ayudaText = useTranslatedText("¿En qué podemos ayudarte?");
  const mensajeText = useTranslatedText("Mensaje");
  const consultarText = useTranslatedText("Consultar");
  
  // Placeholders traducidos
  const nombrePlaceholderText = useTranslatedText("Jane Smith");
  const emailPlaceholderText = useTranslatedText("user@example.com");
  const direccionPlaceholderText = useTranslatedText("Escribe aquí tu dirección completa");
  const telefonoPlaceholderText = useTranslatedText("809-234-5678");
  const mensajePlaceholderText = useTranslatedText("Escribe tu mensaje");
  
  // Servicios traducidos
  const devWebText = useTranslatedText("Dev Web");
  const devMobileText = useTranslatedText("Dev Mobile");
  const identidadMarcaText = useTranslatedText("Identidad de marca");
  const proyectosProductosText = useTranslatedText("Proyectos o Productos");
  const hostingText = useTranslatedText("Hosting");
  const socialMediaText = useTranslatedText("Social media");
  const otrosText = useTranslatedText("Otros");
  
  // Mensajes toast traducidos
  const mensajeEnviadoText = useTranslatedText("Mensaje enviado");
  const contactoProtoText = useTranslatedText("Nos pondremos en contacto contigo pronto.");

  const servicios = [
    devWebText,
    devMobileText, 
    identidadMarcaText,
    proyectosProductosText,
    hostingText,
    socialMediaText,
    otrosText
  ];

  const handleServiceChange = (servicio: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        servicios: [...prev.servicios, servicio]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        servicios: prev.servicios.filter(s => s !== servicio)
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);

    try {
      // Insertar en la base de datos
      const { data, error } = await supabase
        .from('contact_form_submissions')
        .insert([{
          nombre: formData.nombre,
          email: formData.email,
          direccion: formData.direccion,
          telefono: formData.telefono,
          servicios: formData.servicios,
          mensaje: formData.mensaje
        }])
        .select()
        .single();

      if (error) {
        console.error('Error al guardar el formulario:', error);
        toast({
          title: "Error",
          description: "No se pudo enviar el formulario. Inténtalo de nuevo.",
          variant: "destructive",
        });
        return;
      }

      // Enviar notificación por email
      try {
        await supabase.functions.invoke('send-notification-email', {
          body: {
            type: 'contact_form',
            data: {
              id: data.id,
              nombre: formData.nombre,
              email: formData.email,
              direccion: formData.direccion,
              telefono: formData.telefono,
              servicios: formData.servicios.join(', '),
              mensaje: formData.mensaje,
              created_at: new Date().toISOString()
            }
          }
        });
      } catch (emailError) {
        console.error('Error al enviar notificación por email:', emailError);
        // No mostrar error al usuario ya que el formulario se guardó correctamente
      }

      toast({
        title: mensajeEnviadoText,
        description: contactoProtoText,
      });

      // Reset form
      setFormData({
        nombre: "",
        email: "",
        direccion: "",
        telefono: "",
        servicios: [],
        mensaje: ""
      });

    } catch (error) {
      console.error('Error general:', error);
      toast({
        title: "Error",
        description: "No se pudo enviar el formulario. Inténtalo de nuevo.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-20 bg-techno-dark relative">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-techno-purple/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-5xl font-bold text-techno-purple mb-4">
              {emailTitleText}
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            <div className="grid md:grid-cols-2 gap-8">
              {/* Personal Information */}
              <div className="space-y-6">
                <div>
                  <label className="block text-white mb-2 font-medium">
                    {nombreText}<span className="text-techno-purple">*</span>
                  </label>
                  <Input
                    type="text"
                    required
                    placeholder={nombrePlaceholderText}
                    value={formData.nombre}
                    onChange={(e) => setFormData(prev => ({...prev, nombre: e.target.value}))}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-techno-purple"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">
                    {emailText}<span className="text-techno-purple">*</span>
                  </label>
                  <Input
                    type="email"
                    required
                    placeholder={emailPlaceholderText}
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-techno-purple"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">
                    {direccionText}<span className="text-techno-purple">*</span>
                  </label>
                  <Input
                    type="text"
                    required
                    placeholder={direccionPlaceholderText}
                    value={formData.direccion}
                    onChange={(e) => setFormData(prev => ({...prev, direccion: e.target.value}))}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-techno-purple"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">
                    {telefonoText}<span className="text-techno-purple">*</span>
                  </label>
                  <Input
                    type="tel"
                    required
                    placeholder={telefonoPlaceholderText}
                    value={formData.telefono}
                    onChange={(e) => setFormData(prev => ({...prev, telefono: e.target.value}))}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-techno-purple"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Services and Message */}
              <div className="space-y-6">
                <div>
                  <label className="block text-white mb-4 font-medium">
                    {ayudaText}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {servicios.map((servicio, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          id={`servicio-${index}`}
                          checked={formData.servicios.includes(servicio)}
                          onCheckedChange={(checked) => handleServiceChange(servicio, checked as boolean)}
                          className="border-gray-600 data-[state=checked]:bg-techno-purple data-[state=checked]:border-techno-purple"
                          disabled={isSubmitting}
                        />
                        <label
                          htmlFor={`servicio-${index}`}
                          className="text-gray-300 text-sm cursor-pointer"
                        >
                          {servicio}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-white mb-2 font-medium">
                    {mensajeText}<span className="text-techno-purple">*</span>
                  </label>
                  <Textarea
                    required
                    placeholder={mensajePlaceholderText}
                    rows={6}
                    value={formData.mensaje}
                    onChange={(e) => setFormData(prev => ({...prev, mensaje: e.target.value}))}
                    className="bg-gray-800/50 border-gray-600 text-white placeholder-gray-400 focus:border-techno-purple resize-none"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>

            <div className="text-center">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="bg-button-gradient hover:opacity-90 transition-all duration-300 animate-glow px-12 py-4 text-lg disabled:opacity-50"
              >
                {isSubmitting ? "Enviando..." : consultarText}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
